import React, { useState, useEffect } from "react";
import Cta from "@qlik/qlik/src/components/Cta";
import Media from "@qlik/qlik/src/components/Media";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import RichText from "@qlik/qlik/src/components/RichText";
import * as styles from "./styles.module.css";

const FixedImageScrollingText = ({ data }) => {
    const { additionalConfiguration, backgroundColor, cardItems = [], customId } = data ?? {};
    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration, "py-7.5");
    const { sectionColorMapping } = getColorClassName(backgroundColor);

    const [activeMedia, setActiveMedia] = useState(0);

    const cardRefs = cardItems?.map(() => React.createRef());

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const index = cardRefs.findIndex((ref) => ref.current === entry.target);
                        setActiveMedia(index);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0, rootMargin: `-25% 0px -75%` }
        );

        cardRefs?.forEach((ref) => {
            if (ref.current) {
                observer.observe(ref.current);
            }
        });

        return () => {
            if (cardRefs?.length > 0) {
                cardRefs?.forEach((ref) => {
                    if (ref.current) {
                        observer.unobserve(ref.current);
                    }
                });
            }
        };
    }, [cardRefs]);

    return (
        <section className={`${sectionSpacingClassName} ${sectionColorMapping.bgColor}`} id={customId}>
            <div className="container">
                <div className="row lg:justify-between lg:relative lg:items-start	">
                    <div className="col-12 lg:col-6 lg:flex lg:flex-col lg:gap-[113px]">
                        {cardItems?.map((card, index) => {
                            const { title, id, cta, media, eyebrowText, body } = card ?? {};
                            return (
                                <div
                                    key={id}
                                    className="row flex-col py-6.75 lg:flex-row lg:py-0 lg:pr-20 lg:min-h-[500px] lg:flex lg:items-center"
                                    ref={cardRefs?.[index]}
                                >
                                    {media?.[0] && (
                                        <div className="col mb-2.75 lg:hidden">
                                            <Media media={media[0]} className="m-auto px-7.5" />
                                        </div>
                                    )}
                                    <div className="col">
                                        {eyebrowText && (
                                            <span
                                                className={`${sectionColorMapping.primaryTextColor} tag-4 mb-3.25 block`}
                                            >
                                                {eyebrowText}
                                            </span>
                                        )}
                                        {title && (
                                            <h2 className={`${sectionColorMapping.primaryTextColor} title-3`}>
                                                {title}
                                            </h2>
                                        )}
                                        {body?.raw && (
                                            <RichText
                                                data={card}
                                                className={`${styles.bodyWrapper} ${sectionColorMapping.secondaryTextColor} mt-1.75 mb-3.25 lg:mt-3.5 lg:mb-7.5`}
                                            />
                                        )}
                                        <div className="flex gap-3.75 flex-wrap mt-2.5">
                                            {cta?.map((button) => (
                                                <Cta key={button.id} data={button} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="col hidden lg:block lg:sticky lg:top-35 relative lg:h-[500px] lg:w-142.5 lg:mt-0">
                        {cardItems?.map((card, index) => {
                            const { media, id } = card ?? {};
                            return media?.[0] ? (
                                <Media
                                    key={id}
                                    media={media[0]}
                                    className={`${
                                        activeMedia !== index ? "opacity-0" : "opacity-1"
                                    } top-0 absolute duration-1000 transition-opacity m-auto px-7.5 lg:p-0 lg:w-full lg:max-w-[570px] lg:h-full lg:object-contain lg:object-center`}
                                />
                            ) : null;
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FixedImageScrollingText;
